import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Circle, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { searchFood, matchCurrent } from "../lib/api";
import GroupMatch from "./GroupMatch";

const VIGO = [42.2406, -8.7207];
const LIGHT_TILES = "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";

// Establishment types the user can toggle. Each maps to one or more OSM categories.
const TYPE_OPTS = [
  { key: "restaurant", label: "Restaurantes", osm: ["restaurant"] },
  { key: "fast_food", label: "Comida rápida", osm: ["fast_food"] },
  { key: "cafe", label: "Cafeterías", osm: ["cafe"] },
  { key: "bar", label: "Bares", osm: ["bar"] },
  { key: "pub", label: "Pubs", osm: ["pub"] },
  { key: "ice_cream", label: "Heladerías", osm: ["ice_cream"] },
  { key: "bakery", label: "Panaderías", osm: ["bakery", "pastry", "confectionery", "deli"] },
];
const DEFAULT_TYPES = new Set(["restaurant", "fast_food", "cafe", "bar"]);

const CAT_LABEL = {
  restaurant: "Restaurante", fast_food: "Comida rápida", cafe: "Cafetería", bar: "Bar",
  pub: "Pub", food_court: "Food court", ice_cream: "Heladería", biergarten: "Cervecería",
  bakery: "Panadería", deli: "Delicatessen", confectionery: "Confitería", pastry: "Pastelería",
};
const FEATURE_LABEL = {
  outdoor_seating: "Terraza", indoor_seating: "Sala interior", takeaway: "Para llevar",
  delivery: "A domicilio", vegetarian: "Vegetariano", vegan: "Vegano",
  wheelchair: "Accesible", reservation: "Reserva",
};

const pinIcon = L.divIcon({
  className: "fp-pin-wrap",
  html: `<span class="fp-pin"></span>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

function fmtDist(m) {
  if (m == null) return "";
  return m < 1000 ? `${m} m` : `${(m / 1000).toFixed(1)} km`;
}
function fmtRadius(m) {
  return m < 1000 ? `${m} m` : `${(m / 1000).toFixed(1)} km`;
}
function catLabel(c) {
  return CAT_LABEL[c] || (c ? c.replace(/_/g, " ") : "Sitio");
}
function prettyCuisine(c) {
  if (!c) return null;
  return c.split(";")[0].replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}
function socialUrl(base, v) {
  if (!v) return null;
  if (/^https?:\/\//i.test(v)) return v;
  return base + v.replace(/^@/, "");
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function ClickToSet({ onSet }) {
  useMapEvents({ click(e) { onSet([e.latlng.lat, e.latlng.lng]); } });
  return null;
}

// Pans the map to `center` once, when `trigger` changes (geolocation resolve).
function PanOnce({ center, trigger }) {
  const map = useMap();
  useEffect(() => {
    if (trigger) map.setView(center, map.getZoom(), { animate: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);
  return null;
}

// Keeps the (single, reused) discovery mini-map centered on the current pick.
function MiniPan({ lat, lon }) {
  const map = useMap();
  useEffect(() => {
    map.setView([lat, lon], map.getZoom(), { animate: false });
  }, [lat, lon, map]);
  return null;
}

export default function FoodPicker() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("setup"); // "setup" | "discover"
  const [center, setCenter] = useState(VIGO);
  const [radius, setRadius] = useState(1000);
  const [types, setTypes] = useState(DEFAULT_TYPES);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const [queue, setQueue] = useState([]);
  const [idx, setIdx] = useState(0);
  const geoTried = useRef(false);
  const [geoTrigger, setGeoTrigger] = useState(0);

  const [showGroup, setShowGroup] = useState(false);
  const [groupSnap, setGroupSnap] = useState(null);

  const refreshGroup = useCallback(() => {
    matchCurrent().then(setGroupSnap).catch(() => setGroupSnap({ exists: false }));
  }, []);
  useEffect(() => {
    refreshGroup();
    const onFocus = () => refreshGroup();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refreshGroup]);

  useEffect(() => {
    if (geoTried.current || !navigator.geolocation) return;
    geoTried.current = true;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCenter([pos.coords.latitude, pos.coords.longitude]);
        setGeoTrigger((t) => t + 1);
      },
      () => {},
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 }
    );
  }, []);

  const toggleType = useCallback((key) => {
    setTypes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const osmTypes = useMemo(() => {
    const out = [];
    for (const opt of TYPE_OPTS) if (types.has(opt.key)) out.push(...opt.osm);
    return out;
  }, [types]);

  const runSearch = useCallback(async () => {
    if (osmTypes.length === 0) return;
    setLoading(true);
    setErr(null);
    try {
      const list = await searchFood({ lat: center[0], lon: center[1], radius, types: osmTypes });
      setQueue(shuffle(Array.isArray(list) ? list : []));
      setIdx(0);
      setPhase("discover");
    } catch (e) {
      setErr(e.message || "error");
    } finally {
      setLoading(false);
    }
  }, [center, radius, osmTypes]);

  const next = useCallback(() => setIdx((i) => i + 1), []);
  const exit = useCallback(() => navigate("/"), [navigate]);
  const restart = useCallback(() => { setPhase("setup"); setQueue([]); setIdx(0); }, []);

  const current = queue[idx];
  const remaining = Math.max(0, queue.length - idx);

  const links = useMemo(() => {
    if (!current) return [];
    const out = [];
    out.push({ label: "Cómo llegar", href: `https://www.google.com/maps/dir/?api=1&destination=${current.lat},${current.lon}` });
    out.push({ label: "Fotos", href: `https://www.google.com/search?tbm=isch&q=${encodeURIComponent(current.name + " " + (current.city || "") + " restaurante")}` });
    if (current.website) out.push({ label: "Web", href: current.website });
    const ig = socialUrl("https://instagram.com/", current.instagram);
    if (ig) out.push({ label: "Instagram", href: ig });
    const fb = socialUrl("https://facebook.com/", current.facebook);
    if (fb) out.push({ label: "Facebook", href: fb });
    if (current.phone) out.push({ label: "Llamar", href: `tel:${current.phone.replace(/\s+/g, "")}` });
    return out;
  }, [current]);

  const addrLine = useMemo(() => {
    if (!current) return null;
    const parts = [current.address, [current.postcode, current.city].filter(Boolean).join(" ")].filter(Boolean);
    return parts.length ? parts.join(" · ") : null;
  }, [current]);

  return (
    <div className="fp-root">
      {showGroup ? (
        <GroupMatch
          center={center}
          radius={radius}
          osmTypes={osmTypes}
          onExit={() => { setShowGroup(false); refreshGroup(); }}
        />
      ) : null}
      <style>{`
        .fp-root{position:fixed;inset:0;display:flex;flex-direction:column;background:#fafafa;color:#111;
          font-family:Inter,ui-sans-serif,system-ui,-apple-system,sans-serif}
        .fp-bar{display:flex;align-items:center;justify-content:space-between;gap:.5rem;padding:.85rem 1.1rem;
          border-bottom:1px solid #ececec;background:#fff;z-index:10}
        .fp-title{font-size:1.02rem;font-weight:600;letter-spacing:-.01em}
        .fp-sub{font-size:.72rem;color:#8a8a8a;margin-top:1px}
        .fp-x{border:none;background:transparent;color:#9a9a9a;font-size:1.4rem;line-height:1;cursor:pointer;padding:.2rem .4rem;border-radius:8px}
        .fp-x:hover{background:#f1f1f1;color:#444}

        .fp-map-wrap{position:relative;flex:1;min-height:0}
        .fp-map{position:absolute;inset:0;height:100%;width:100%}
        .fp-hint{position:absolute;top:12px;left:50%;transform:translateX(-50%);z-index:500;
          background:rgba(255,255,255,.92);backdrop-filter:blur(6px);border:1px solid #ececec;border-radius:999px;
          padding:.4rem .9rem;font-size:.74rem;color:#555;box-shadow:0 2px 10px rgba(0,0,0,.06)}
        .fp-pin{display:block;width:18px;height:18px;border-radius:999px;background:#111;border:3px solid #fff;
          box-shadow:0 0 0 2px rgba(0,0,0,.15),0 3px 8px rgba(0,0,0,.35)}

        .fp-panel{background:#fff;border-top:1px solid #ececec;padding:.9rem 1.2rem 1.2rem;
          box-shadow:0 -6px 20px rgba(0,0,0,.04)}
        .fp-plabel{font-size:.72rem;letter-spacing:.04em;text-transform:uppercase;color:#9a9a9a;margin-bottom:.5rem}
        .fp-chips{display:flex;flex-wrap:wrap;gap:.45rem;margin-bottom:.9rem}
        .fp-chip{border:1px solid #e0e0e0;background:#fff;color:#555;border-radius:999px;
          padding:.4rem .8rem;font-size:.8rem;cursor:pointer;transition:all .12s;white-space:nowrap}
        .fp-chip.on{background:#111;border-color:#111;color:#fff}
        .fp-row{display:flex;align-items:baseline;justify-content:space-between;margin-bottom:.4rem}
        .fp-row-label{font-size:.8rem;color:#666}
        .fp-row-val{font-size:1.05rem;font-weight:600;font-variant-numeric:tabular-nums}
        .fp-range{width:100%;accent-color:#111;height:24px;cursor:pointer}
        .fp-ticks{display:flex;justify-content:space-between;font-size:.62rem;color:#b3b3b3;margin-top:-2px}
        .fp-cta{margin-top:.9rem;width:100%;border:none;border-radius:14px;background:#111;color:#fff;
          font-size:1rem;font-weight:600;padding:.95rem;cursor:pointer;transition:transform .06s,opacity .2s}
        .fp-cta:active{transform:scale(.985)}
        .fp-cta:disabled{opacity:.45;cursor:default}
        .fp-err{color:#c0362c;font-size:.78rem;margin-top:.6rem;text-align:center}
        .fp-group{margin-top:.6rem;width:100%;border:1px solid #d8d8d8;background:#fff;color:#333;border-radius:14px;
          font-size:.92rem;font-weight:600;padding:.85rem;cursor:pointer;transition:transform .06s}
        .fp-group:active{transform:scale(.985)}
        .fp-group:disabled{opacity:.55;cursor:default}
        .fp-group-on{border-color:#111;background:#111;color:#fff}

        /* discovery */
        .fp-stage{flex:1;min-height:0;overflow-y:auto;display:flex;flex-direction:column;align-items:center;
          padding:1.1rem 1.1rem 1.4rem;background:radial-gradient(120% 80% at 50% 0%,#fff 0%,#f4f4f5 100%)}
        .fp-count{font-size:.72rem;letter-spacing:.08em;text-transform:uppercase;color:#9a9a9a;margin:.3rem 0 .8rem}
        .fp-card{width:100%;max-width:440px;background:#fff;border:1px solid #ececec;border-radius:22px;
          overflow:hidden;box-shadow:0 12px 40px rgba(0,0,0,.08)}
        .fp-mini{height:150px;width:100%;background:#eee}
        .fp-mini .leaflet-container{background:#eef0f2}
        .fp-body{padding:1.2rem 1.35rem 1.35rem}
        .fp-badge{display:inline-block;font-size:.68rem;font-weight:600;letter-spacing:.03em;color:#5a5a5a;
          background:#f2f2f2;border-radius:999px;padding:.28rem .7rem;text-transform:uppercase}
        .fp-name{font-size:1.55rem;font-weight:700;letter-spacing:-.02em;line-height:1.15;margin:.6rem 0 .3rem}
        .fp-meta{font-size:.9rem;color:#666}
        .fp-meta b{color:#111;font-weight:600}
        .fp-addr{font-size:.82rem;color:#8a8a8a;margin-top:.45rem}
        .fp-hours{font-size:.8rem;color:#557;margin-top:.5rem;background:#f6f7fb;border-radius:8px;padding:.4rem .6rem;
          white-space:pre-wrap;word-break:break-word}
        .fp-feats{display:flex;flex-wrap:wrap;gap:.4rem;margin-top:.8rem}
        .fp-feat{font-size:.72rem;color:#3d6b3d;background:#eef7ee;border:1px solid #dcefdc;border-radius:999px;padding:.24rem .6rem}
        .fp-links{display:flex;gap:.5rem .9rem;margin-top:1rem;flex-wrap:wrap}
        .fp-link{font-size:.82rem;color:#111;text-decoration:none;border-bottom:1px solid #ddd;padding-bottom:1px}
        .fp-link:hover{border-color:#111}

        .fp-actions{display:flex;gap:.7rem;width:100%;max-width:440px;margin-top:1.2rem}
        .fp-btn{flex:1;border-radius:14px;padding:.95rem;font-size:.95rem;font-weight:600;cursor:pointer;transition:transform .06s}
        .fp-btn:active{transform:scale(.985)}
        .fp-btn-next{border:none;background:#111;color:#fff}
        .fp-btn-exit{border:1px solid #d8d8d8;background:#fff;color:#333}
        .fp-btn-exit:hover{border-color:#111}

        .fp-empty{text-align:center;max-width:340px;margin:auto}
        .fp-empty h3{font-size:1.2rem;font-weight:700;margin-bottom:.4rem}
        .fp-empty p{font-size:.88rem;color:#777;margin-bottom:1.1rem}

        .fp-spinner{position:absolute;inset:0;z-index:600;display:flex;align-items:center;justify-content:center;
          background:rgba(250,250,250,.7);backdrop-filter:blur(2px);color:#555;font-size:.85rem}
      `}</style>

      {phase === "setup" ? (
        <>
          <div className="fp-bar">
            <div>
              <div className="fp-title">¿Dónde comemos?</div>
              <div className="fp-sub">Punto, radio y qué buscamos</div>
            </div>
            <button className="fp-x" onClick={exit} aria-label="Salir">×</button>
          </div>

          <div className="fp-map-wrap">
            <div className="fp-hint">Toca el mapa para mover el punto</div>
            <MapContainer center={center} zoom={14} className="fp-map" zoomControl={true}>
              <TileLayer url={LIGHT_TILES} attribution='&copy; OpenStreetMap &copy; CARTO' />
              <ClickToSet onSet={setCenter} />
              <Marker position={center} icon={pinIcon} />
              <Circle center={center} radius={radius}
                pathOptions={{ color: "#111", weight: 1.5, fillColor: "#111", fillOpacity: 0.06 }} />
              <PanOnce center={center} trigger={geoTrigger} />
            </MapContainer>
            {loading ? <div className="fp-spinner">Buscando sitios…</div> : null}
          </div>

          <div className="fp-panel">
            <div className="fp-plabel">¿Qué buscamos?</div>
            <div className="fp-chips">
              {TYPE_OPTS.map((opt) => (
                <button
                  key={opt.key}
                  className={`fp-chip ${types.has(opt.key) ? "on" : ""}`}
                  onClick={() => toggleType(opt.key)}
                  aria-pressed={types.has(opt.key)}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="fp-row">
              <span className="fp-row-label">Radio de búsqueda</span>
              <span className="fp-row-val">{fmtRadius(radius)}</span>
            </div>
            <input className="fp-range" type="range" min={300} max={6000} step={100}
              value={radius} onChange={(e) => setRadius(Number(e.target.value))} />
            <div className="fp-ticks"><span>300 m</span><span>3 km</span><span>6 km</span></div>

            <button className="fp-cta" onClick={runSearch} disabled={loading || osmTypes.length === 0}>
              {loading ? "Buscando…" : osmTypes.length === 0 ? "Elige al menos un tipo" : "Buscar sitios"}
            </button>
            {err ? <div className="fp-err">No se pudo buscar ({err}). Inténtalo de nuevo.</div> : null}

            {(() => {
              const exists = groupSnap && groupSnap.exists;
              const youJoined = exists && groupSnap.youJoined;
              const locked = exists && (groupSnap.phase === "locked" || groupSnap.phase === "finished");
              let label = "👥 Crear grupo";
              let disabled = false;
              let resume = false;
              if (youJoined) { label = "👥 Reanudar grupo"; resume = true; }
              else if (exists && !locked) { label = "👥 Unirse al grupo"; }
              else if (exists && locked) { label = "🔒 Grupo en curso"; disabled = true; }
              return (
                <button
                  className={`fp-group ${resume ? "fp-group-on" : ""}`}
                  onClick={() => setShowGroup(true)}
                  disabled={disabled}
                >
                  {label}
                </button>
              );
            })()}
          </div>
        </>
      ) : (
        <>
          <div className="fp-bar">
            <div>
              <div className="fp-title">A ver qué toca</div>
              <div className="fp-sub">{queue.length} sitios en {fmtRadius(radius)}</div>
            </div>
            <button className="fp-x" onClick={restart} aria-label="Cambiar zona">×</button>
          </div>

          <div className="fp-stage">
            {current ? (
              <>
                <div className="fp-count">{remaining} por ver</div>
                <div className="fp-card">
                  <MapContainer
                    center={[current.lat, current.lon]} zoom={16} className="fp-mini"
                    zoomControl={false} attributionControl={false} dragging={false}
                    scrollWheelZoom={false} doubleClickZoom={false} keyboard={false} touchZoom={false}
                  >
                    <TileLayer url={LIGHT_TILES} />
                    <Marker position={[current.lat, current.lon]} icon={pinIcon} />
                    <MiniPan lat={current.lat} lon={current.lon} />
                  </MapContainer>

                  <div className="fp-body">
                    <span className="fp-badge">{catLabel(current.category)}</span>
                    <div className="fp-name">{current.name}</div>
                    <div className="fp-meta">
                      {prettyCuisine(current.cuisine) ? <><b>{prettyCuisine(current.cuisine)}</b> · </> : null}
                      a {fmtDist(current.distanceM)}
                    </div>
                    {addrLine ? <div className="fp-addr">{addrLine}</div> : null}

                    {current.features && current.features.length ? (
                      <div className="fp-feats">
                        {current.features.map((f) => (
                          <span className="fp-feat" key={f}>{FEATURE_LABEL[f] || f}</span>
                        ))}
                      </div>
                    ) : null}

                    {current.openingHours ? <div className="fp-hours">🕒 {current.openingHours}</div> : null}

                    <div className="fp-links">
                      {links.map((l) => (
                        <a className="fp-link" key={l.label} href={l.href} target="_blank" rel="noreferrer">
                          {l.label} ↗
                        </a>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="fp-actions">
                  <button className="fp-btn fp-btn-exit" onClick={exit}>Salir</button>
                  <button className="fp-btn fp-btn-next" onClick={next}>No, siguiente</button>
                </div>
              </>
            ) : (
              <div className="fp-empty">
                {queue.length === 0 ? (
                  <>
                    <h3>Nada por aquí</h3>
                    <p>No hay sitios de ese tipo en la zona. Prueba con más tipos o un radio mayor.</p>
                  </>
                ) : (
                  <>
                    <h3>Se acabaron</h3>
                    <p>Ya has visto todos los sitios de la zona. Amplía el radio o mueve el punto.</p>
                  </>
                )}
                <div className="fp-actions" style={{ justifyContent: "center" }}>
                  <button className="fp-btn fp-btn-exit" onClick={exit}>Salir</button>
                  <button className="fp-btn fp-btn-next" onClick={restart}>Cambiar zona</button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
